import './debugOverlay.css';
import { STYLES } from '../core/styles.js';
import { createWeapon, weaponIds } from '../core/weapons.js';
import { heatWords } from './heatWords.js';

/**
 * Development overlay: both health bars, the current state, the strike
 * currently out, and buttons to put the fighter into any state, throw a real
 * strike, or hit it with a test blow.
 *
 * It reads the machine and the health model and never decides anything itself —
 * the state buttons call force(), which is the machine's explicit test door.
 * Legality is shown rather than enforced: a button the machine would refuse a
 * request() to is dimmed and carries the refusal as its tooltip, so the graph
 * can be inspected by hovering it.
 */

/** [label, wellness damage, options] */
const TEST_HITS = [
  ['jab', 4, {}],
  ['body', 12, { consciousnessMultiplier: 1.3 }],
  ['head', 12, { consciousnessMultiplier: 4 }],
  ['heavy', 26, {}],
];

export function createDebugOverlay({
  combatant,
  opponent,
  fight,
  input,
  ai,
  impact,
  match,
  arenas,
  crowd,
  campaign,
  career = null,
  audio = null,
  container = document.body,
}) {
  const { machine, health, animation, strikes } = combatant;

  const root = el('div', 'debug-overlay');
  // Collapsed to its title bar until somebody asks for it. It stopped being
  // the game's UI at step 64 and went back to being the developer's window, and
  // a developer's window does not open itself over the game.
  root.dataset.collapsed = 'true';

  // --- header ---------------------------------------------------------------
  const head = el('div', 'debug-overlay__head');
  head.append(text('span', '', 'debug'));
  const toggle = el('button', 'debug-overlay__toggle');
  toggle.textContent = '–';
  toggle.title = 'Collapse (`)';
  head.append(toggle);
  root.append(head);

  // --- the round ------------------------------------------------------------
  // First, because it decides whether anything else on this panel is even
  // being driven: on the marks and after a finish, the controls do nothing.
  let matchLine = null;
  if (match) {
    const section = el('div', 'debug-overlay__section');
    matchLine = text('div', 'debug-overlay__state-name', '');
    section.append(matchLine);
    const again = el('button');
    again.textContent = 'reset round';
    again.title = 'Back to the marks: both fighters restored, meters emptied';
    again.addEventListener('click', () => match.reset());
    const grid = el('div', 'debug-grid');
    grid.append(again);
    section.append(grid);
    root.append(section);
  }

  // --- the ladder -----------------------------------------------------------
  // Where the player stands, who is next, and whether the card is open. None
  // of it is visible from watching two people hit each other, and a rank that
  // moved while you were in the ring is exactly the kind of thing that reads as
  // a bug until you can see it happen.
  const updateLadder = campaign ? ladderPanel(root, campaign, career) : () => {};

  // --- the arena ------------------------------------------------------------
  // Which one is loaded, and the parts of it that move. Everything else about
  // an arena is visible by looking at it; a train thirteen seconds away is not.
  const updateArena = arenas ? arenaPanel(root, arenas, crowd) : () => {};

  // --- sound ----------------------------------------------------------------
  // The master, and what it last played. The mute lives here rather than on a
  // screen for the same reason the arena swap does: this is the developer's
  // window, and the game has no options menu to put one in (§7).
  const updateAudio = audio ? audioPanel(root, audio) : () => {};

  // --- styles ---------------------------------------------------------------
  // The one thing on this panel that changes the fighter rather than reporting
  // on them. Styles decide the move list and how the fight can be won, and
  // neither is legible from watching two mannequins, so both are printed.
  const updateStyles = combatant.loadout
    ? stylePanel(root, combatant, () => buildMoveButtons())
    : () => {};

  // --- health ---------------------------------------------------------------
  const healthSection = el('div', 'debug-overlay__section');
  // Who *you* are. The player is a roster entry like everybody else now, and a
  // name that is applied and never shown is a name nobody can tell is applied.
  const youHeading = el('div', 'debug-overlay__label');
  const youName = text('span', '', combatant.name ?? combatant.id);
  youHeading.append(youName);
  healthSection.append(youHeading);
  const wellnessLabel = labelRow(healthSection, 'wellness');
  const wellnessFill = bar(healthSection, 'wellness');
  const consciousnessLabel = labelRow(healthSection, 'consciousness');
  const { fill: consciousnessFill, ceiling } = bar(healthSection, 'consciousness', true);
  const danger = text('div', 'debug-overlay__danger', 'in danger');
  healthSection.append(danger);
  // Momentum sits with the health bars because it is read the same way: a
  // number you glance at between exchanges to decide what to do next.
  const momentumLabel = combatant.momentum ? labelRow(healthSection, 'momentum') : null;
  const momentumFill = combatant.momentum ? bar(healthSection, 'momentum') : null;
  root.append(healthSection);

  // --- weapon ---------------------------------------------------------------
  // What is in their hands and how many swings are left in it. The move set
  // silently swapping is exactly the kind of thing that has to be legible, and
  // the buttons are how a weapon gets into a hand before the crowd hands one out.
  const updateWeapon = combatant.arm ? weaponPanel(root, combatant) : () => {};

  // --- heat -----------------------------------------------------------------
  // Momentum and heat sit apart on purpose. They are read for different
  // reasons, they move on different things, and a fighter can be full of one
  // and empty of the other — which is the entire point of having both.
  const updateHeat = combatant.heat ? heatPanel(root, combatant.heat) : () => {};

  // --- state ----------------------------------------------------------------
  const stateSection = el('div', 'debug-overlay__section');
  const stateRow = el('div', 'debug-overlay__state');
  const stateName = text('span', 'debug-overlay__state-name', machine.state);
  const stateTime = text('span', 'debug-overlay__meta', '');
  stateRow.append(stateName, stateTime);
  stateSection.append(stateRow);
  const clipLine = text('div', 'debug-overlay__meta', '');
  stateSection.append(clipLine);
  const strikeLine = text('div', 'debug-overlay__meta', '');
  stateSection.append(strikeLine);
  const comboLine = combatant.combo ? text('div', 'debug-overlay__meta', 'no chain') : null;
  if (comboLine) stateSection.append(comboLine);
  const grappleLine = fight?.grapple ? text('div', 'debug-overlay__meta', 'no clinch') : null;
  if (grappleLine) stateSection.append(grappleLine);
  root.append(stateSection);

  // --- input ----------------------------------------------------------------
  // The move vector and the held verbs, straight off the device layer. Input is
  // the one system with nothing on screen to show for itself when it works, so
  // without this the only way to tell a dead binding from a dead gamepad is to
  // guess.
  const updateInput = input ? inputPanel(root, input) : () => {};

  // --- strikes --------------------------------------------------------------
  const strikeSection = el('div', 'debug-overlay__section');
  strikeSection.append(labelOnly('throw strike'));
  const strikeGrid = el('div', 'debug-grid');
  const moveButtons = el('div', 'debug-grid');

  /**
   * One button per move the fighter currently knows. Rebuilt whenever the
   * styles change, because a move list that does not follow the loadout is a
   * button that throws nothing.
   */
  const gated = new Map();

  function buildMoveButtons() {
    moveButtons.replaceChildren();
    gated.clear();
    for (const name of combatant.loadout?.moves ?? strikes.names()) {
      const move = strikes.get(name);
      const button = el('button');
      button.textContent = name;
      // Frame data, the way a fighting game prints it: the three numbers that
      // decide whether a move is worth throwing.
      button.title = `${move.name} · ${move.strength} · ${move.damage} damage · ${move.startup}/${move.active}/${move.recovery} frames · ${move.reach}m`;
      // request(), not force() — an illegal attack should fail here too.
      button.addEventListener('click', () => combatant.throwStrike(name));
      moveButtons.append(button);
      // A move that wants a reeling target is refused from here like anywhere
      // else, so the button has to say so rather than quietly doing nothing —
      // the state buttons have carried their own refusal since step 5.
      if (move.requiresStun) gated.set(name, { button, title: button.title });
    }
  }
  buildMoveButtons();

  /** Dims the stun-gated buttons, and puts the reason in the tooltip. */
  function updateMoveButtons() {
    for (const [name, { button, title }] of gated) {
      const refusal = combatant.stunRefusal?.(name) ?? null;
      const legal = String(refusal === null);
      if (button.dataset.legal === legal) continue;
      button.dataset.legal = legal;
      button.title = refusal ? `${title} — ${refusal}` : title;
    }
  }

  const boxes = el('button');
  boxes.textContent = 'boxes';
  boxes.title = 'Show the hit and hurt volumes riding the rig';
  boxes.addEventListener('click', () => {
    const next = !combatant.hitboxes.visible;
    // Both fighters, or you can see the fist but not what it is arriving on.
    for (const each of fight?.combatants ?? [combatant]) each.setDebugVisible(next);
    boxes.dataset.current = String(next);
  });
  strikeGrid.append(boxes);

  const reach = el('button');
  reach.textContent = 'reach (r)';
  reach.title = combatant.reach
    ? `Rings on the floor at each strike's measured reach — ${combatant.reach.list
        .map(({ move, reach: r }) => `${move} ${r}m`)
        .join(', ')}`
    : 'Rings on the floor at each strike’s measured reach';
  reach.addEventListener('click', toggleReach);
  strikeGrid.append(reach);

  function toggleReach() {
    const next = !combatant.reachVisible;
    // Both fighters again: spacing is a fact about the pair, not about one of
    // them, and one ring on its own tells you nothing about who is safe.
    for (const each of fight?.combatants ?? [combatant]) each.setReachVisible?.(next);
    reach.dataset.current = String(next);
  }
  strikeSection.append(moveButtons, strikeGrid);
  const hitLine = text('div', 'debug-overlay__meta', 'no hits yet');
  strikeSection.append(hitLine);
  // Hitstop and shake are over in a tenth of a second, which is exactly long
  // enough to argue about and too short to see. Numbers make them tunable.
  const feelLine = impact ? text('div', 'debug-overlay__meta', '') : null;
  if (feelLine) strikeSection.append(feelLine);
  root.append(strikeSection);

  fight?.onHit((hit) => {
    hitLine.textContent = `${hit.attacker} → ${hit.target} ${hit.strike} on ${hit.part}${
      hit.blocked ? ' (blocked)' : ''
    }: −${hit.wellness.toFixed(1)} well, −${hit.consciousness.toFixed(1)} cons · ${
      hit.reaction
    } · ${hit.knockback?.toFixed(2) ?? '0.00'}m back${hit.punished ? ' · WHIFF PUNISH' : ''}${
      hit.chainIndex ? ` · hit ${hit.chainIndex + 1} of a chain (x${hit.comboScale})` : ''
    }`;
    hitLine.classList.toggle('debug-overlay__meta--warn', Boolean(hit.blocked));
  });

  // --- state buttons --------------------------------------------------------
  const statesSection = el('div', 'debug-overlay__section');
  statesSection.append(labelOnly('force state'));
  const stateGrid = el('div', 'debug-grid');
  const stateButtons = new Map();
  for (const name of machine.states()) {
    const button = el('button');
    button.textContent = name;
    button.addEventListener('click', () => machine.force(name));
    stateGrid.append(button);
    stateButtons.set(name, button);
  }
  statesSection.append(stateGrid);
  root.append(statesSection);

  // --- damage buttons -------------------------------------------------------
  const damageSection = el('div', 'debug-overlay__section');
  damageSection.append(labelOnly('apply damage'));
  const damageGrid = el('div', 'debug-grid');
  for (const [label, amount, options] of TEST_HITS) {
    const button = el('button');
    button.textContent = label;
    button.title = `${amount} wellness, x${
      options.consciousnessMultiplier ?? health.config.consciousnessMultiplier
    } consciousness`;
    button.addEventListener('click', () => health.applyDamage(amount, options));
    damageGrid.append(button);
  }
  if (combatant.momentum) {
    const meter = el('button');
    meter.textContent = 'meter';
    meter.title = 'Fill the momentum meter, so blazin can be tried without earning it';
    meter.addEventListener('click', () => combatant.momentum.add(combatant.momentum.max));
    const blaze = el('button');
    blaze.textContent = 'blazin (u)';
    blaze.title = 'Spend a full meter. Refused at anything less.';
    blaze.addEventListener('click', () => combatant.ignite());
    damageGrid.append(meter, blaze);
  }

  const choke = el('button');
  choke.textContent = 'choke';
  choke.title = 'Drains consciousness only, leaving the body untouched';
  choke.addEventListener('click', () => health.drainConsciousness(25));
  const reset = el('button');
  reset.textContent = 'reset';
  reset.addEventListener('click', () => health.reset());
  damageGrid.append(choke, reset);
  damageSection.append(damageGrid);
  root.append(damageSection);

  // --- the other fighter ----------------------------------------------------
  // Only ever read, never driven. The point of it is to watch a landed strike
  // arrive on someone else's bars, which is the one thing the attacker's own
  // panel cannot show.
  const updateOpponent = opponent ? opponentPanel(root, opponent, ai, combatant) : () => {};

  container.append(root);

  toggle.addEventListener('click', collapse);
  window.addEventListener('keydown', onKey);

  function collapse() {
    const next = root.dataset.collapsed !== 'true';
    root.dataset.collapsed = String(next);
    toggle.textContent = next ? '+' : '–';
  }

  function onKey(event) {
    if (event.key === '`') collapse();
    // Not a bound game action, so the input layer leaves KeyR alone and there
    // is nothing to negotiate over it.
    if (event.code === 'KeyR' && !event.repeat) toggleReach();
  }

  // Only the values that changed are written, so the overlay is not rebuilding
  // strings for every one of the eleven buttons every frame.
  const last = {};

  /** A fighter's id, as the name a human would use for them. */
  const nameOf = (id) => {
    if (id === combatant.id) return combatant.name ?? id;
    if (opponent && id === opponent.id) return opponent.name ?? id;
    return id;
  };

  let youWere = null;

  function update() {
    if (root.dataset.collapsed === 'true') return;

    if (youWere !== combatant.name) {
      youWere = combatant.name;
      setText(youName, youWere ?? combatant.id);
    }

    const f = health.fractions();
    write(wellnessFill.style, 'width', pct(f.wellness));
    write(consciousnessFill.style, 'width', pct(f.consciousness));
    write(ceiling.style, 'left', pct(f.ceiling));

    setText(wellnessLabel, health.wellness.toFixed(0));
    setText(
      consciousnessLabel,
      `${health.consciousness.toFixed(0)}${health.regenerating ? ' ↑' : ''}`
    );

    updateLadder();

    if (matchLine) {
      setText(
        matchLine,
        match.phase === 'intro'
          ? `on the marks · ${match.countdown.toFixed(1)}s`
          : match.phase === 'over'
            ? match.drawn
              ? `a draw${match.decidedOn ? ` on ${match.decidedOn}` : ''}`
              : `${nameOf(match.winner)} wins by ${
                  match.verdict === 'decision'
                    ? `decision on ${match.decidedOn}`
                    : (match.finishedBy ?? 'knockout')
                }`
            // The clock, because a round that can run out is a round the player
            // has to be able to see running out.
            : `fighting · ${match.remaining.toFixed(0)}s`
      );
      if (last.phase !== match.phase) {
        last.phase = match.phase;
        root.dataset.phase = match.phase;
      }
    }

    if (momentumFill) {
      const meter = combatant.momentum;
      // While blazin the meter is spent, so the same track counts the blaze
      // down instead — an empty bar would say nothing is happening.
      write(
        momentumFill.style,
        'width',
        pct(meter.blazin ? meter.blazinLeft / meter.config.blazin.duration : meter.fraction)
      );
      setText(
        momentumLabel,
        meter.blazin
          ? `BLAZIN ${meter.blazinLeft.toFixed(1)}s`
          : `${meter.value.toFixed(0)}${meter.full ? ' — ready' : ''}${
              meter.decaying ? ' ↓' : ''
            } · charisma ${meter.charisma} × heat ${meter.drive.toFixed(2)}`
      );
      if (last.blazin !== meter.blazin) {
        last.blazin = meter.blazin;
        root.dataset.blazin = String(meter.blazin);
      }
    }

    if (last.danger !== health.inDanger) {
      last.danger = health.inDanger;
      root.dataset.danger = String(health.inDanger);
    }

    // Left unassigned here on purpose — the legality block below owns
    // last.state, and runs after this one in the same frame.
    //
    // The clip is watched as well as the state, because they no longer move
    // together: a jab cancelled into a cross never leaves `strike`, and the
    // whole point of the move data is that the two look different.
    const clip = animation?.clip ?? machine.clip;
    if (last.state !== machine.state || last.clip !== clip) {
      last.clip = clip;
      stateName.textContent = machine.state;
      const missing = animation?.clipMissing;
      clipLine.textContent = missing
        ? `clip "${machine.clip}" missing`
        : `clip ${clip}${machine.loop ? ' (loop)' : ''}${
            machine.definition.interruptible ? '' : ' · locked'
          }`;
      clipLine.classList.toggle('debug-overlay__meta--warn', Boolean(missing));
    }

    // clipEnded() unlocks an uninterruptible state without changing which state
    // it is, so legality has to be recomputed on that flip as well.
    if (last.state !== machine.state || last.clipDone !== machine.clipDone) {
      last.state = machine.state;
      last.clipDone = machine.clipDone;
      for (const [name, button] of stateButtons) {
        const refusal = machine.refusal(name);
        button.dataset.legal = String(refusal === null);
        button.dataset.current = String(name === machine.state);
        button.title = refusal ?? `request() would allow ${machine.state} → ${name}`;
      }
    }

    const seconds = machine.timeInState.toFixed(1);
    if (last.seconds !== seconds) {
      last.seconds = seconds;
      stateTime.textContent = `${seconds}s`;
    }

    // The strike's whole lifecycle on one line: the active window opening and
    // closing against the punch, and then — if it touched nobody — the punish
    // window it left behind, which is the part you cannot otherwise see at all.
    const missed = strikes.active && strikes.missed;
    setText(
      strikeLine,
      strikes.active
        ? `${strikes.strike} · ${strikes.phase}${missed ? ' · MISSED' : ''} · frame ${Math.round(
            strikes.progress * strikes.shape.frames
          )}/${strikes.shape.frames}`
        : combatant.vulnerable
          ? `whiffed — open for ${combatant.vulnerableFor.toFixed(2)}s (x${combatant.vulnerability})`
          : 'no strike out'
    );
    if (last.live !== strikes.windowOpen) {
      last.live = strikes.windowOpen;
      strikeLine.classList.toggle('debug-overlay__meta--live', strikes.windowOpen);
    }
    // The cancel window is a sixth of a second and invisible. Without it on
    // screen there is no way to tell a combo route that does not exist from a
    // window you keep missing by two frames.
    // The clinch: who has whom, how long is left, and whether the one window
    // out of it is open. None of that is visible on a pair of blocky
    // mannequins standing still.
    if (grappleLine) {
      const g = fight.grapple;
      setText(
        grappleLine,
        g.active
          ? `${g.holder.id} holds ${g.held.id} · ${g.remaining.toFixed(2)}s${
              g.escapeOpen ? ' · ESCAPE OPEN' : g.escapeSpent ? ' · attempt spent' : ''
            }`
          : 'no clinch'
      );
      if (!g.active && opponent?.stunned) {
        // The follow-up window. Invisible otherwise, and the whole point of it
        // is that it is short.
        setText(grappleLine, `${opponent.id} reeling — finisher for ${opponent.stunnedFor.toFixed(2)}s`);
      }
      grappleLine.classList.toggle(
        'debug-overlay__meta--live',
        g.escapeOpen || (!g.active && Boolean(opponent?.stunned))
      );
    }

    if (comboLine) {
      const chain = combatant.combo;
      setText(
        comboLine,
        chain.length
          ? `chain ${chain.chain.join(' → ')}${
              chain.open ? ` · cancel ${chain.windowLeft.toFixed(2)}s into ${chain.follows(
                chain.chain[chain.chain.length - 1]
              ).join('/') || 'nothing'}` : chain.pending ? ' · follow-up in the air' : ''
            }${chain.buffered ? ` · buffered ${chain.buffered}` : ''}`
          : `no chain${chain.buffered ? ` · buffered ${chain.buffered}` : ''}`
      );
      comboLine.classList.toggle('debug-overlay__meta--live', chain.open);
    }

    const exposed = combatant.vulnerable || missed;
    if (last.exposed !== exposed) {
      last.exposed = exposed;
      strikeLine.classList.toggle('debug-overlay__meta--warn', exposed);
      root.dataset.vulnerable = String(exposed);
    }

    if (feelLine) {
      const frozen = impact.frozen;
      setText(
        feelLine,
        frozen || impact.shaking
          ? `hitstop ${impact.hitstop.toFixed(3)}s${impact.shaking ? ' · shaking' : ''}`
          : 'settled'
      );
      feelLine.classList.toggle('debug-overlay__meta--live', frozen);
    }

    updateStyles();
    updateMoveButtons();
    updateArena();
    updateAudio();
    updateWeapon();
    updateHeat();
    updateInput();
    updateOpponent();
  }

  return {
    element: root,
    update,
    dispose() {
      window.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}

/**
 * Style selection, and what the choice actually bought.
 *
 * Clicking a style toggles it on or off; the **first one on is primary**, and
 * clicking an equipped style that is not primary promotes it, because "which
 * one is primary" is the only decision here that changes how the fight ends and
 * it needs to be one click away.
 *
 * Returns its per-frame update.
 */
function stylePanel(root, combatant, onChange) {
  const section = el('div', 'debug-overlay__section');
  section.append(labelOnly('styles'));

  const grid = el('div', 'debug-grid');
  const buttons = new Map();
  for (const [id, style] of Object.entries(STYLES.styles)) {
    const button = el('button');
    button.textContent = style.name.split(' ')[0].toLowerCase();
    button.addEventListener('click', () => pick(id));
    grid.append(button);
    buttons.set(id, button);
  }
  section.append(grid);

  const koLine = text('div', 'debug-overlay__meta', '');
  const statLine = text('div', 'debug-overlay__meta', '');
  const moveLine = text('div', 'debug-overlay__meta', '');
  const errorLine = text('div', 'debug-overlay__meta debug-overlay__meta--warn', '');
  errorLine.hidden = true;
  section.append(koLine, statLine, moveLine, errorLine);
  root.append(section);

  function pick(id) {
    const current = combatant.loadout.ids;
    let next;
    if (!current.includes(id)) next = [...current, id];
    else if (current[0] !== id) next = [id, ...current.filter((other) => other !== id)];
    else next = current.filter((other) => other !== id);

    try {
      combatant.setStyles(next);
      errorLine.hidden = true;
    } catch (error) {
      // A refused loadout — too many, or none — is shown rather than swallowed.
      // The overlay is the one place a bad pick should be visible.
      errorLine.textContent = error.message;
      errorLine.hidden = false;
    }
  }

  let last = null;
  return function update() {
    const loadout = combatant.loadout;
    const signature = loadout.ids.join(',');
    if (signature === last) return;
    last = signature;

    for (const [id, button] of buttons) {
      const index = loadout.ids.indexOf(id);
      button.dataset.current = String(index === 0);
      button.dataset.legal = String(index >= 0);
      button.title =
        index === 0
          ? `primary — wins by ${STYLES.styles[id].ko.describe}`
          : index > 0
            ? 'equipped; click to make primary'
            : STYLES.styles[id].ko.describe;
    }

    setText(koLine, `wins by ${loadout.ko.describe}`);
    setText(
      statLine,
      Object.entries(loadout.stats)
        .map(([stat, value]) => `${stat} ${value.toFixed(2)}`)
        .join(' · ')
    );
    setText(moveLine, loadout.moves.join(', '));
    onChange?.(loadout);
  };
}

/**
 * What is in the fighter's hands.
 *
 * A weapon swaps the strike set out from under the move buttons, which is the
 * kind of change that has to be visible or it reads as the jab having changed
 * its mind. The durability is the whole economy of the thing, so it is a count
 * rather than a bar — five swings and two swings are different situations, and
 * a bar at 40% does not say which.
 */
function weaponPanel(root, combatant) {
  const section = el('div', 'debug-overlay__section');
  section.append(labelOnly('weapon'));
  const line = text('div', 'debug-overlay__state-name', 'empty handed');
  const meta = text('div', 'debug-overlay__meta', '');
  section.append(line, meta);

  const grid = el('div', 'debug-grid');
  for (const id of weaponIds()) {
    const button = el('button');
    button.textContent = id;
    button.title = `Put a ${id} in their hands`;
    button.addEventListener('click', () => combatant.arm(createWeapon(id)));
    grid.append(button);
  }
  const drop = el('button');
  drop.textContent = 'drop';
  drop.title = 'Empty handed again';
  drop.addEventListener('click', () => combatant.disarm());
  grid.append(drop);
  section.append(grid);
  root.append(section);

  return function update() {
    const weapon = combatant.weapon;
    root.dataset.armed = String(Boolean(combatant.armed));
    drop.dataset.legal = String(Boolean(weapon));
    if (!combatant.armed) {
      setText(line, 'empty handed');
      setText(meta, '');
      return;
    }
    setText(line, `${weapon.name} · ${weapon.durability}/${weapon.max}`);
    setText(
      meta,
      `${weapon.move} · ${weapon.spec.damage} dmg · reach ${weapon.spec.reach} · unblockable`
    );
  };
}

/**
 * Heat, and the short list of why.
 *
 * The number on its own says almost nothing — 44 is not a fact anybody can act
 * on. What makes it legible is the five most recent things that moved it, with
 * the fighter each happened against, which is also the only way to tell at a
 * glance that a source has quietly stopped firing.
 */
function heatPanel(root, heat) {
  const section = el('div', 'debug-overlay__section');
  const head = labelRow(section, 'heat');
  const fill = bar(section, 'heat');
  const level = text('div', 'debug-overlay__meta', '');
  section.append(level);

  const list = el('div', 'debug-overlay__events');
  section.append(list);
  root.append(section);

  let last = null;
  return function update() {
    write(fill.style, 'width', pct(heat.fraction));
    setText(head, String(Math.round(heat.value)));
    // The band rather than the number, because the band is what the crowd and
    // the interference rules actually read.
    root.dataset.heat = heat.level;
    setText(level, heat.level);

    // Rebuilt only when the list has actually changed. It changes a handful of
    // times a round; rebuilding five rows every frame to show the same five
    // rows is the kind of thing that makes an overlay cost more than the game.
    const signature = heat.events.map((event) => `${event.type}@${event.at}`).join('|');
    if (signature === last) return;
    last = signature;

    list.replaceChildren(
      ...heat.events.map((event) => {
        const row = el('div', 'debug-overlay__event');
        row.dataset.rise = String(event.rise);
        const sign = event.delta > 0 ? '+' : '';
        row.textContent = `${sign}${event.delta.toFixed(0)} ${heatWords(event)}`;
        row.title = `${event.type} against ${event.opponent ?? 'nobody'}, at ${event.at.toFixed(1)}s`;
        return row;
      })
    );
    if (!heat.events.length) {
      list.append(text('div', 'debug-overlay__meta', 'nothing worth watching yet'));
    }
  };
}

/**
 * The ladder, the player's place on it, and what that place buys.
 *
 * Shows a window around the player rather than the whole roster: thirteen rows
 * in a debug panel is a wall, and the only ones that matter are the ones within
 * reach. The top is always shown, because that is what the act ends against and
 * what the climb is *for*.
 *
 * Returns its per-frame update.
 */
function ladderPanel(root, campaign, career = null) {
  const section = el('div', 'debug-overlay__section');
  section.append(labelOnly('ladder'));

  const standing = text('div', 'debug-overlay__state-name', '');
  const recordLine = text('div', 'debug-overlay__meta', '');
  const gateLine = text('div', 'debug-overlay__meta', '');
  const nextLine = text('div', 'debug-overlay__meta', '');
  section.append(standing, recordLine, gateLine, nextLine);

  const list = el('div', 'debug-overlay__events');
  section.append(list);

  /**
   * Throwing the career away, which takes two clicks on purpose.
   *
   * A reload continues a career; the *only* thing that ends one is somebody
   * asking for it, and a one-click button sitting under the ladder is a career
   * lost to a mis-click. The second click is the confirmation, and it lapses
   * after a few seconds so a button left saying "really?" cannot be armed by
   * something somebody did a minute ago.
   */
  const savedLine = career ? text('div', 'debug-overlay__meta', '') : null;
  if (savedLine) section.append(savedLine);
  if (career?.restart) {
    const grid = el('div', 'debug-grid');
    const wipe = el('button');
    let armed = 0;
    const disarm = () => {
      armed = 0;
      wipe.textContent = 'new career';
      wipe.dataset.legal = 'true';
      wipe.title = 'Start again from the bottom of the ladder. Asks twice.';
    };
    disarm();
    wipe.addEventListener('click', () => {
      if (armed && Date.now() < armed) {
        disarm();
        career.restart();
        return;
      }
      armed = Date.now() + 4000;
      wipe.textContent = 'really? click again';
      wipe.dataset.legal = 'false';
      setTimeout(disarm, 4000);
    });
    grid.append(wipe);
    section.append(grid);
  }

  root.append(section);

  let last = null;
  return function update() {
    if (savedLine) {
      const status = career.store?.status;
      setText(
        savedLine,
        !career.store?.available
          ? 'not saving — no storage'
          : status?.ok
            ? 'career saved'
            : `fresh career — ${status?.reason ?? 'nothing loaded'}`
      );
    }

    const rank = campaign.rank;
    const gate = campaign.gate;
    const next = campaign.next;

    setText(standing, `rank ${rank} of ${campaign.ladder.size}`);
    setText(
      recordLine,
      `${campaign.wins}W ${campaign.losses}L · ${campaign.fights} fights${
        campaign.streak ? ` · ${Math.abs(campaign.streak)} ${campaign.streak > 0 ? 'won' : 'lost'} in a row` : ''
      }`
    );
    // The distance, not just the fact. A player who cannot see how far off they
    // are cannot see that winning brought it closer.
    setText(
      gateLine,
      gate.open
        ? `contender · ${campaign.act.name} is open${campaign.actComplete ? ' · taken' : ''}`
        : `${gate.short} place${gate.short === 1 ? '' : 's'} off the card (top ${gate.need})`
    );
    gateLine.classList.toggle('debug-overlay__meta--live', gate.open);
    setText(nextLine, `next: ${next.name ?? 'nobody'}${next.kind === 'act' ? ' — for the card' : ''}`);

    // Rebuilt only when the order or the ranks have actually changed. The
    // ladder churns a handful of times a round; redrawing eight rows every
    // frame to show the same eight rows is how an overlay costs more than the
    // game it is watching.
    const window = rowsAround(campaign);
    const signature = window.map((entry) => `${entry.rank}:${entry.id}:${entry.record.wins}-${entry.record.losses}`).join('|');
    if (signature === last) return;
    last = signature;

    list.replaceChildren(
      ...window.map((entry) => {
        const row = el('div', 'debug-overlay__event');
        row.dataset.rise = String(entry.player);
        row.dataset.gap = String(entry.gap ?? false);
        const mark = entry.player ? '▸' : ' ';
        row.textContent = `${mark}${String(entry.rank).padStart(2)} ${entry.name}`;
        row.title = entry.player
          ? `You — ${entry.record.wins}W ${entry.record.losses}L`
          : `${entry.name} · ${entry.tier} · ${entry.styles.join(' + ')} · ${entry.record.wins}W ${entry.record.losses}L`;
        return row;
      })
    );
  };
}

/**
 * The rows worth drawing: the top of the ladder, and the player's neighbours.
 *
 * A gap row is inserted when the two do not meet, so the list never lies about
 * being contiguous.
 */
function rowsAround(campaign, reach = 2) {
  const ranks = campaign.ladder.ranks;
  const at = campaign.rank - 1;
  const top = ranks.slice(0, Math.max(campaign.gate.need, 1));
  const near = ranks.slice(Math.max(0, at - reach), at + reach + 1);
  const rows = [...top];
  if (near[0] && near[0].rank > top[top.length - 1].rank + 1) {
    rows.push({ id: '…', name: '…', rank: '', gap: true, player: false, record: { wins: 0, losses: 0 }, styles: [] });
  }
  for (const entry of near) {
    if (rows.some((row) => row.id === entry.id)) continue;
    rows.push(entry);
  }
  return rows;
}

/**
 * Which arena is loaded, and anything in it with a clock.
 *
 * Swapping is the point. Two arenas exist and they test different things — the
 * test arena is four identical walls you can be thrown at from any direction,
 * the platform is a place with a hole in it — and having to edit a constant and
 * reload to see the other one is how one of them quietly stops working.
 *
 * The train half hides itself in an arena that has no train, rather than being
 * built per-arena, because the panel outlives any one of them.
 */
/**
 * The master, a mute, and the last few things that made a noise.
 *
 * The rolling list is the whole reason this panel is worth having: audio is the
 * one system whose failures are silent by definition, and "nothing played" and
 * "something played and you could not hear it" look identical from a chair.
 */
function audioPanel(root, audio) {
  const section = el('div', 'debug-overlay__section');
  section.append(labelOnly('sound'));

  const state = text('div', 'debug-overlay__state-name', '');
  section.append(state);

  const grid = el('div', 'debug-grid');
  const mute = el('button');
  mute.title = 'Silence everything, and keep it silent across a reload';
  mute.addEventListener('click', () => audio.toggle());
  grid.append(mute);
  for (const step of [0, 0.35, 0.7, 1]) {
    const button = el('button');
    button.textContent = `${Math.round(step * 100)}`;
    button.title = `Master volume ${Math.round(step * 100)}%`;
    button.addEventListener('click', () => audio.setVolume(step));
    grid.append(button);
  }
  section.append(grid);

  const played = el('div', 'debug-overlay__events');
  section.append(played);
  root.append(section);

  return () => {
    mute.textContent = audio.muted ? 'unmute' : 'mute';
    mute.dataset.on = String(audio.muted);
    setText(
      state,
      `${audio.muted ? 'muted' : `${Math.round(audio.volume * 100)}%`} · crowd ${Math.round(
        audio.crowd * 100
      )}%${audio.audible ? '' : ' · nothing behind it'}`
    );
    played.replaceChildren(
      ...audio.recent.slice(0, 5).map((cue) => {
        const row = el('div', 'debug-overlay__event');
        row.textContent = `${cue.name}  ×${cue.rate.toFixed(2)}`;
        return row;
      })
    );
  };
}

function arenaPanel(root, arenas, crowd = null) {
  const section = el('div', 'debug-overlay__section');
  section.append(labelOnly('arena'));

  const grid = el('div', 'debug-grid');
  const buttons = new Map();
  for (const entry of arenas.list()) {
    const button = el('button');
    button.textContent = entry.name.split(' ')[0].toLowerCase();
    button.title = `Load ${entry.name}, and keep it whatever the next card says`;
    button.addEventListener('click', () => arenas.load(entry.id));
    grid.append(button);
    buttons.set(entry.id, button);
  }
  // A hand-picked arena is pinned (see `createArenaHost`), so the cards stop
  // choosing. This hands the choice back; the next booking loads its venue.
  const cards = arenas.unpin ? el('button') : null;
  if (cards) {
    cards.textContent = 'by card';
    cards.title = 'Let the card decide: the next fight loads wherever it is booked';
    cards.addEventListener('click', () => arenas.unpin());
    grid.append(cards);
  }
  section.append(grid);

  const trainLine = text('div', 'debug-overlay__state-name', '');
  const trainMeta = text('div', 'debug-overlay__meta', '');
  const trainGrid = el('div', 'debug-grid');
  const call = el('button');
  call.textContent = 'call train';
  call.title = 'Bring the next service forward to now';
  call.addEventListener('click', () => arenas.current?.train?.summon());
  const clear = el('button');
  clear.textContent = 'clear track';
  clear.title = 'Back to an empty platform';
  clear.addEventListener('click', () => arenas.current?.train?.reset());
  trainGrid.append(call, clear);
  section.append(trainLine, trainMeta, trainGrid);

  // The crowd is the one thing on screen that reports on heat rather than on
  // itself, and "are they actually reacting" is hard to read off seventy
  // bodies bobbing in the dark.
  const crowdLine = crowd ? text('div', 'debug-overlay__meta', '') : null;
  if (crowdLine) section.append(crowdLine);
  // What the crowd has thrown in and not yet been picked up.
  const floorLine = crowd ? text('div', 'debug-overlay__meta debug-overlay__meta--warn', '') : null;
  if (floorLine) section.append(floorLine);
  root.append(section);

  let lastId = null;
  return function update() {
    updateCrowd();
    const arena = arenas.current;
    if (arena?.id !== lastId) {
      lastId = arena?.id ?? null;
      for (const [id, button] of buttons) button.dataset.current = String(id === lastId);
    }
    if (cards) cards.dataset.current = String(!arenas.pinned);

    // An arena with no train shows no train controls, rather than showing dead
    // ones. There is exactly one arena with a train and there will be others
    // without.
    const train = arena?.train ?? null;
    for (const node of [trainLine, trainMeta, trainGrid]) node.hidden = !train;
    if (!train) return;

    setText(trainLine, `train · ${train.phase}`);
    const eta = train.eta;
    setText(
      trainMeta,
      train.present
        ? `in the station · x ${train.position.toFixed(1)}`
        : `${eta === null ? '—' : `${eta.toFixed(1)}s`} · headlights ${(train.glow * 100).toFixed(0)}%`
    );
    call.dataset.legal = String(train.phase === 'away');
    root.dataset.train = train.phase;
  };

  function updateCrowd() {
    if (!crowdLine) return;
    const here = arenas.current?.crowd;
    crowdLine.hidden = !here;
    if (!here) return;
    const grab = crowd.grab;

    setText(
      crowdLine,
      grab
        ? `HOLDING ${grab.id} — ${grab.reason} ${grab.forWhom ?? ''} · ${grab.left.toFixed(1)}s`
        : `crowd ${here.count} · ${(crowd.energy * 100).toFixed(0)}% · on ${
            crowd.favourite?.id ?? 'nobody'
          }${crowd.cooling > 0 ? ` · hands back in ${crowd.cooling.toFixed(0)}s` : ''}`
    );
    crowdLine.classList.toggle('debug-overlay__meta--warn', Boolean(grab));

    if (!floorLine) return;
    const items = crowd.ground?.items ?? [];
    floorLine.hidden = items.length === 0;
    setText(
      floorLine,
      items
        .map((item) => `${item.weapon.id} on the floor for ${item.forWhom ?? 'anybody'} · ${item.left.toFixed(0)}s`)
        .join(' | ')
    );
  }
}

/**
 * The live action set. Returns its per-frame update.
 *
 * Reads the input layer and never drives it — the point is to see what the
 * devices are actually producing, so a binding that does nothing can be told
 * apart from a control layer that ignores it.
 */
function inputPanel(root, input) {
  const section = el('div', 'debug-overlay__section');
  section.append(labelOnly('input'));
  const device = text('div', 'debug-overlay__meta', '');
  const moveLine = text('div', 'debug-overlay__meta', '');
  const actionLine = text('div', 'debug-overlay__meta', '');
  section.append(device, moveLine, actionLine);
  root.append(section);

  return function update() {
    setText(device, input.gamepad ? `gamepad: ${input.gamepad}` : 'keyboard only');
    const { x, y } = input.move;
    setText(moveLine, `move ${x.toFixed(2)}, ${y.toFixed(2)} (x right, y forward)`);
    const held = input.actions;
    setText(actionLine, held.length ? held.join(' · ') : 'nothing held');
    actionLine.classList.toggle('debug-overlay__meta--live', held.length > 0);
  };
}

/** A read-only strip for the other fighter. Returns its per-frame update. */
function opponentPanel(root, opponent, ai, player) {
  const section = el('div', 'debug-overlay__section');
  // The *name*, not the id. The id is the slot and never moves; the name is
  // whoever the ladder booked into it, and it changes every round.
  const heading = el('div', 'debug-overlay__label');
  const who = text('span', '', opponent.name ?? opponent.id);
  const tierTag = text('span', '', '');
  heading.append(who, tierTag);
  section.append(heading);

  const wellnessLabel = labelRow(section, 'wellness');
  const wellnessFill = bar(section, 'wellness');
  const consciousnessLabel = labelRow(section, 'consciousness');
  const { fill: consciousnessFill, ceiling } = bar(section, 'consciousness', true);
  const stateLine = text('div', 'debug-overlay__meta', '');
  section.append(stateLine);
  // The number the whole fight turns on and the one you cannot read off a
  // three-quarter view: two fighters a metre apart and half a metre apart look
  // nearly the same on screen, and only one of those is inside a punch.
  const rangeLine = player && ai ? text('div', 'debug-overlay__meta', '') : null;
  if (rangeLine) section.append(rangeLine);
  root.append(section);

  const { health, machine } = opponent;
  let named = null;
  return function update() {
    if (named !== opponent.name) {
      named = opponent.name;
      setText(who, named ?? opponent.id);
      setText(tierTag, ai?.tier ?? '');
    }
    if (rangeLine) {
      const gap = Math.hypot(
        player.position.x - opponent.position.x,
        player.position.z - opponent.position.z
      );
      const wants = ai.preferredRange;
      setText(rangeLine, `gap ${gap.toFixed(2)}m · wants ${wants.toFixed(2)}m`);
      rangeLine.classList.toggle('debug-overlay__meta--live', gap <= wants);
    }

    const f = health.fractions();
    write(wellnessFill.style, 'width', pct(f.wellness));
    write(consciousnessFill.style, 'width', pct(f.consciousness));
    write(ceiling.style, 'left', pct(f.ceiling));
    setText(wellnessLabel, health.wellness.toFixed(0));
    setText(
      consciousnessLabel,
      `${health.consciousness.toFixed(0)}${health.regenerating ? ' ↑' : ''}`
    );
    setText(
      stateLine,
      `${machine.state}${opponent.momentum ? ` · meter ${opponent.momentum.value.toFixed(0)}${
        opponent.momentum.blazin ? ' BLAZIN' : ''
      }` : ''}${opponent.vulnerable ? ` · open ${opponent.vulnerableFor.toFixed(2)}s` : ''}${
        health.inDanger ? ' · in danger' : ''
      }${health.isOut ? ' · out' : ''}${
        // What the AI decided to do and how long it has left to run — a jab
        // that never connects reads as broken until you can see that it simply
        // decided to hold its ground. The tier sits in the panel's own heading
        // now, next to the name, because the two change together.
        ai ? ` · ${ai.choice} ${ai.remaining.toFixed(1)}s` : ''
      }`
    );
  };
}

// --- tiny DOM helpers --------------------------------------------------------

function el(tag, className) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function text(tag, className, content) {
  const node = el(tag, className);
  node.textContent = content;
  return node;
}

function labelOnly(name) {
  const row = el('div', 'debug-overlay__label');
  row.append(text('span', '', name));
  return row;
}

function labelRow(parent, name) {
  const row = labelOnly(name);
  const value = text('span', '', '');
  row.append(value);
  parent.append(row);
  return value;
}

function bar(parent, kind, withCeiling = false) {
  const track = el('div', 'debug-bar');
  const fill = el('div', `debug-bar__fill debug-bar__fill--${kind}`);
  track.append(fill);
  let ceiling = null;
  if (withCeiling) {
    ceiling = el('div', 'debug-bar__ceiling');
    track.append(ceiling);
  }
  parent.append(track);
  return withCeiling ? { fill, ceiling } : fill;
}

const pct = (fraction) => `${Math.max(0, Math.min(1, fraction)) * 100}%`;

function write(style, property, value) {
  if (style[property] !== value) style[property] = value;
}

function setText(node, value) {
  if (node.textContent !== value) node.textContent = value;
}
